import { useQuery } from '@apollo/react-hooks';
import { Box, Heading3, StackView, Text } from 'localmed-core';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { DiButton, RadioGroup } from '../../../../../../../shared/components/DiComponents';
import { DiGenericLoading } from '../../../../../../../shared/components/DiLayout';
import { LM_CLIENT_CONTEXT } from '../../../../../../../shared/constants';
import BackLink from '../../../BackLink';
import { IStepProps } from '../../../schedulingWizard';
import { BackButton, ContentWrapper, Wrapper } from '../../../styles';
import query from './ProvidersListQuery.graphql';
import SchedulingProviderCard from './SchedulingProviderCard';

interface Props extends IStepProps {
  setIsOpen: (value: boolean) => void;
}

export default function SchedulingProviderSelection({ wizard, setIsOpen }: Props) {
  const [provider, setProvider] = useState(wizard?.values.provider);
  const { office, reasonForVisit } = wizard?.values || {};
  const { data, loading } = useQuery(query, {
    variables: { officeId: office?.id, reasonForVisitId: reasonForVisit?.id },
    skip: office == null,
    context: LM_CLIENT_CONTEXT,
  });

  useEffect(() => {
    if (wizard?.trackSchedulingEvent) {
      wizard.trackSchedulingEvent(
        'Scheduling Provider Selection Screen',
        'SchedulingProviderSelection'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!wizard) return null;

  const { provider: currentFormProvider } = wizard.values;

  const Save = () => {
    wizard.form.batch(() => {
      wizard.form.change('provider', provider);
      wizard.form.change('queryProvider', provider);
    });
    // Go back to Time selection step if the provider is changed
    setTimeout(() => wizard.gotoStep('SCHEDULING_TIME_SELECTION_STEP'), 0);
  };

  if (loading) return <DiGenericLoading />;

  if (!data) return null;
  const { nodes } = data.providers;
  return (
    <Wrapper>
      <BackLink
        onClick={() => {
          setIsOpen(false);
        }}
      />
      <ContentWrapper>
        <Box textAlign={{ _: 'center', lg: 'left' }} width="100%">
          <StackView spacing="md">
            <Heading3>
              <FormattedMessage
                id="scheduling.SchedulingProviderSelection.title"
                defaultMessage="Which provider would you like to see?"
              />
            </Heading3>
            <Text>
              <FormattedMessage
                id="scheduling.SchedulingProviderSelection.providerSelectCaption"
                defaultMessage="Select one:"
              />
            </Text>
            <RadioGroup
              name="provider"
              items={nodes}
              itemDisplayComponent={SchedulingProviderCard}
              onChange={(value) => {
                setProvider(value);
              }}
              value={provider}
              columns={2}
            />
          </StackView>
        </Box>
      </ContentWrapper>
      <Box display="flex" justifyContent={{ _: 'center', lg: 'space-between' }}>
        <BackButton
          onClick={() => {
            setIsOpen(false);
          }}
          display={{ _: 'none', lg: 'inherit' }}
          variant="subtle"
          size="xl"
        >
          <FormattedMessage
            id="scheduling.SchedulingProviderSelection.cancelButton"
            defaultMessage="Cancel"
          />
        </BackButton>
        <DiButton onClick={Save} size="xl" disabled={currentFormProvider?.id === provider?.id}>
          <FormattedMessage
            id="scheduling.SchedulingProviderSelection.saveButton"
            defaultMessage="Save"
          />
        </DiButton>
      </Box>
    </Wrapper>
  );
}
